import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Modal2 from 'components/commons/modal/Modal2';
import Loading from 'components/commons/loading/Loading';
import { ModalTypeProps } from 'components/commons/Main';
import { getUserStorage } from 'components/features/utils/utils';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import {
  Agreed,
  DisclaimerProps,
  getDisclaimerAPI,
  setAgreedStorage,
  useDisclaimer,
} from 'hooks/useDisclaimer';
import { formatDateForString } from 'utils/formatter';
import {
  DisclaimerItem,
  UpdateUserDisclaimerParam,
  updateUserDisclaimer,
} from 'config/api';

const checkAllAgreed = (agreed: Agreed[] | null): boolean => {
  return agreed !== null && agreed.every(agreed => agreed.agreed);
};

type Status = boolean;

const toggleIsAgreedChecked_ = (prev: Status): Status => {
  return !prev;
};

const ModalContent = ({
  isShow,
  close,
  disclaimers,
  disclaimersList,
  notAgreedIDs,
}: {
  isShow: boolean;
  close: () => void;
  disclaimers: DisclaimerProps[];
  disclaimersList: DisclaimerProps[][];
  notAgreedIDs: UpdateUserDisclaimerParam;
}) => {
  const { pushRouting } = useRouting();
  const user = getUserStorage();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [agreeStatus, setAgreeStatus] = useState<Status>(false);

  const isNewCustomer = disclaimersList.every(disclaimer => {
    const baseDisclaimer = disclaimer.find(item => item.notificationID === '0');
    return baseDisclaimer !== undefined && !baseDisclaimer.agreed;
  });

  const toggleIsAgreedChecked = () =>
    setAgreeStatus(prev => toggleIsAgreedChecked_(prev));

  // MEMO: 親規約 -> externalLinkの値によって外部サイトorアセプラ内でページ遷移
  //       子規約 -> お知らせの重要なお知らせ詳細に遷移
  const toInvestorIdAgreement = (disclaimer: DisclaimerProps) => {
    if (
      disclaimer.notificationID !== null &&
      disclaimer.notificationID !== '0'
    ) {
      pushRouting(
        { path: `/notificationDetail/3/${disclaimer.notificationID}` },
        true
      );
    } else if (disclaimer.externalLink) {
      if (disclaimer.content === null) return;
      window.open(disclaimer.content, '_blank');
    } else {
      pushRouting({ path: `/importantnotification/${disclaimer.ID}` }, true);
    }
  };

  const continue_ = async () => {
    setIsLoading(true);
    await updateUserDisclaimer(notAgreedIDs);
    const ret = await getDisclaimerAPI();

    const disclaimers: DisclaimerProps[] = ret;
    const disclaimersList = convertDisclaimers(disclaimers);

    const agreedList = disclaimersList.map(disclaimers => {
      const isAgreed = disclaimers.every(list => list.agreed);
      return { id: disclaimers[0].ID, agreed: isAgreed };
    }) as Agreed[];
    setAgreedStorage(agreedList);

    setIsLoading(false);
    close();
    if (user !== null && user.NewUserAndInverstorIDNotIssued) {
      pushRouting({ path: '/investoridexplanation' }, true);
    }
  };

  const notAgreedDisclaimers = disclaimersList
    .map(disclaimerList => {
      const baseDisclaimer = disclaimerList.find(
        list => list.notificationID === '0'
      );
      if (baseDisclaimer === undefined) return null;

      if (!baseDisclaimer.agreed) {
        return baseDisclaimer;
      } else {
        const list = disclaimerList.filter(list => !list.agreed);
        return list.length === 0 ? null : list;
      }
    })
    .filter(x => x)
    .flat() as DisclaimerProps[];

  const sortedDisclaimers = notAgreedDisclaimers.sort((first, second) => {
    const firstDate = new Date(first.updTime).getTime();
    const secondDate = new Date(second.updTime).getTime();

    if (firstDate > secondDate) {
      return 1;
    } else if (firstDate < secondDate) {
      return -1;
    } else {
      return 0;
    }
  });

  const getRevisionDate = (displayDisclaimer: DisclaimerProps) => {
    const sameIdDisclaimers = disclaimers.filter(
      disclaimer => disclaimer.ID === displayDisclaimer.ID
    );

    const latestUpdTimeDisclaimer = sameIdDisclaimers.reduce(
      (prev, current) => {
        const prevUpdTime = new Date(prev.updTime).getTime();
        const currentUpdTime = new Date(current.updTime).getTime();

        if (prevUpdTime === currentUpdTime) {
          return prev;
        } else if (prevUpdTime > currentUpdTime) {
          return prev;
        } else if (prevUpdTime < currentUpdTime) {
          return current;
        } else {
          return prev;
        }
      },
      sameIdDisclaimers[0] as DisclaimerProps
    );

    return latestUpdTimeDisclaimer.updTime;
  };

  return (
    <>
      <Modal2 isShow={isShow}>
        <div className="px-6 py-4 overflow-y-auto text-bluegray-700">
          <div className="font-bold text-base pb-4">重要なお知らせ</div>
          <div className="text-sm">
            {isNewCustomer ? (
              <>
                みんかぶアセットプランナーをご利用いただくには下記の内容に同意いただく必要があります。
                <br />
                内容をご確認の上、記載内容に同意いただける場合はチェックを入れてお進みください。
              </>
            ) : (
              <>
                以下、利用規約または同意内容に変更がありました。ご確認の上、記載内容に同意いただける場合はチェックを入れてお進みください。
              </>
            )}
          </div>
          <div className="px-4 pb-2">
            <div className="mt-4 text-sm font-bold">必ずお読みください</div>
            {sortedDisclaimers.map((disclaimer, i) => {
              const date = getRevisionDate(disclaimer);

              return (
                <>
                  <div className="flex flex-row items-center mt-2 text-sm text-link">
                    <div
                      className="cursor-pointer"
                      onClick={(): void => toInvestorIdAgreement(disclaimer)}>
                      {disclaimer.notification !== null
                        ? disclaimer.notification.title
                        : disclaimer.title}
                    </div>
                    {disclaimer.externalLink && (
                      <div className="icon-external-link ml-2"></div>
                    )}
                  </div>
                  <div className="mb-2 text-xs text-bluegray-500">
                    {formatDateForString(date, 'yyyy/M/d')} 改訂
                  </div>
                  {sortedDisclaimers.length - 1 !== i ? (
                    <>
                      <div className="border-b border-bluegray-300"></div>
                    </>
                  ) : (
                    <></>
                  )}
                </>
              );
            })}
          </div>
          <div
            className="rounded py-2 px-4 my-4 bg-bluegray-50 flex items-center cursor-pointer"
            onClick={toggleIsAgreedChecked}>
            <div
              className={`icon-checkmark mr-3 ${
                agreeStatus ? 'text-blue-700' : 'text-gray-300'
              }`}></div>
            同意する
          </div>
          <button
            className={`${'font-bold rounded-lg px-2 py-4 justify-center w-full bg-bluegray-600 text-white flex'} ${
              agreeStatus ? '' : 'opacity-10'
            }`}
            style={agreeStatus ? {} : { cursor: 'not-allowed' }}
            onClick={continue_}
            disabled={!agreeStatus}>
            {isLoading ? (
              <>
                <Loading
                  loading
                  circularLoading
                  style={{ width: '45px', height: '27px' }}
                />
              </>
            ) : (
              '続ける'
            )}
          </button>
        </div>
      </Modal2>
    </>
  );
};

// MEMO: disclaimersをID毎の配列に変換
const convertDisclaimers = (
  disclaimers: DisclaimerProps[]
): DisclaimerProps[][] => {
  const disclaimerIDs_ = disclaimers.map(disclaimer => disclaimer.ID);
  const disclaimerIDs = Array.from(new Set(disclaimerIDs_));

  return disclaimerIDs.map(disclaimerID => {
    return disclaimers.filter(disclaimer => disclaimerID === disclaimer.ID);
  });
};

const AgreeModalInternal = ({
  disclaimers,
  disclaimersList,
  updateModalType,
}: {
  disclaimers: DisclaimerProps[];
  disclaimersList: DisclaimerProps[][];
  updateModalType: () => void;
}) => {
  const location = useLocation();
  const [isShow, { hideModal, showModal }] = useModal();
  const [notAgreedIDs, setNotAgreedIds] = useState<UpdateUserDisclaimerParam>();

  const close = () => {
    hideModal();
    updateModalType();
  };

  useEffect(() => {
    (async () => {
      const isDisclaimersAgreed = disclaimers.every(
        disclaimer => disclaimer.agreed
      );

      if (!isDisclaimersAgreed) {
        const list = disclaimers
          .map(disclaimer => {
            if (disclaimer.agreed) return null;
            return {
              ID: disclaimer.ID,
              NotificationID: disclaimer.notificationID,
            } as DisclaimerItem;
          })
          .filter(x => x) as UpdateUserDisclaimerParam;
        setNotAgreedIds(list);

        return showModal();
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (notAgreedIDs === undefined) return <></>;

  return (
    <ModalContent
      isShow={isShow}
      close={close}
      disclaimers={disclaimers}
      disclaimersList={disclaimersList}
      notAgreedIDs={notAgreedIDs}
    />
  );
};

export const WithAgreeModal = ({
  setModalType,
}: {
  setModalType?: (type: ModalTypeProps) => void;
}): React.ReactElement => {
  const {
    disclaimers: disclaimers_,
    isLoading,
    isRefetching,
  } = useDisclaimer();

  const updateModalType = (): void => {
    if (setModalType) setModalType('advertisement');
  };

  if (disclaimers_ === undefined || isLoading || isRefetching) {
    return <></>;
  }

  const disclaimers: DisclaimerProps[] = disclaimers_;
  const disclaimersList = convertDisclaimers(disclaimers);

  const agreedList = disclaimersList.map(disclaimers => {
    const isAgreed = disclaimers.every(list => list.agreed);
    return { id: disclaimers[0].ID, agreed: isAgreed };
  }) as Agreed[];
  setAgreedStorage(agreedList);

  const isAllAgreed = checkAllAgreed(agreedList);
  if (isAllAgreed) {
    updateModalType();
    return <></>;
  }

  return (
    <AgreeModalInternal
      {...{ disclaimers, disclaimersList, updateModalType }}
    />
  );
};
